import React, { useState } from "react";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Badge,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../authConfig";
import { AppDispatch } from "../store/store";
import { useDispatch } from "react-redux";
import {
  startImpersonation,
  stopImpersonationAction,
  grantTrial,
} from "../store/modules/admin/adminActions";
import { RootState } from "../store/reducers";
import { useSelector } from "react-redux";
import { persistor, store } from "../store/store";
import { clearAccessToken } from "../store/modules/auth/authSlice";
import { logOut } from "../utils/auth";

interface UserAccountMenuProps {
  isImpersonating: boolean;
  impersonatedUserEmail?: string | null;
}

const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  isImpersonating,
  impersonatedUserEmail,
}) => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser: user } = useSelector((state: RootState) => state?.user);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminTab, setAdminTab] = useState(0);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminSuccess, setAdminSuccess] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const handleLogin = (loginType: string) => {
    setAnchorEl(null);
    if (loginType === "popup") {
      instance.loginPopup(loginRequest);
    } else if (loginType === "redirect") {
      instance.loginRedirect(loginRequest);
    }
  };

  const handleLogout = async (logoutType: string) => {
    setAnchorEl(null);

    try {
      // Clear MSAL cache
      await instance.clearCache();

      // Clear Redux state
      store.dispatch(clearAccessToken());

      // Purge persisted state completely
      await persistor.purge();
      await persistor.flush();

      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();

      // Redirect to logout
      logOut(instance);
    } catch (error) {
      console.error("Logout error:", error);
      // Force cleanup on error
      localStorage.clear();
      sessionStorage.clear();
      logOut(instance);
    }
  };

  // Impersonation handlers
  const handleImpersonate = async () => {
    if (!adminEmail.trim()) {
      setAdminError("Please enter an email");
      return;
    }

    try {
      await dispatch(startImpersonation(adminEmail));
      setAdminEmail("");
      setAdminError(null);
      setAdminSuccess("Impersonation started");
      setTimeout(() => setAdminSuccess(null), 3000);
      if (isMobile) {
        setAnchorEl(null);
      }
    } catch (error: any) {
      setAdminError("Impersonation failed: " + error.message);
      setAdminSuccess(null);
    }
  };

  const handleStopImpersonating = async () => {
    try {
      await dispatch(stopImpersonationAction());
      setAnchorEl(null);
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    } catch (error: any) {
      setAdminError("Failed to stop impersonation: " + error.message);
    }
  };

  const handleGrantTrial = async () => {
    if (!adminEmail.trim()) {
      setAdminError("Please enter an email");
      return;
    }

    try {
      await grantTrial(adminEmail);
      setAdminSuccess(`Trial granted successfully to ${adminEmail}`);
      setAdminEmail("");
      setAdminError(null);
      setTimeout(() => setAdminSuccess(null), 3000);
    } catch (error: any) {
      setAdminError(
        `Failed to grant trial: ${error.message || "Unknown error"}`
      );
      setAdminSuccess(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{ color: "white" }}
        title={isAuthenticated ? "Account" : "Sign in"}
      >
        <Badge color="error" variant="dot" invisible={!isImpersonating}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Badge>
      </IconButton>

      {isMobile ? (
        <Dialog open={open} onClose={handleMenuClose} fullWidth maxWidth="xs">
          <DialogTitle>{isAuthenticated ? "Account" : "Sign in"}</DialogTitle>
          <DialogContent>
            {isAuthenticated ? (
              <>
                {isImpersonating && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Currently impersonating
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {impersonatedUserEmail || ""}
                    </Typography>
                  </Box>
                )}
                {account && (
                  <Typography variant="body2" gutterBottom>
                    Logged in as: {account.username || account.name}
                  </Typography>
                )}
                {user?.isAdmin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Admin Actions
                    </Typography>
                    <Button
                      onClick={() => {
                        navigate("/settings/email-templates");
                        handleMenuClose();
                      }}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Email Templates
                    </Button>

                    <Tabs
                      value={adminTab}
                      onChange={(e, v) => setAdminTab(v)}
                      variant="fullWidth"
                    >
                      <Tab label="Impersonate" />
                    </Tabs>

                    {adminSuccess && (
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ mt: 2 }}
                      >
                        {adminSuccess}
                      </Typography>
                    )}

                    {adminTab === 0 && (
                      <Box sx={{ mt: 2 }}>
                        {isImpersonating ? (
                          <Button
                            onClick={handleStopImpersonating}
                            color="error"
                            variant="contained"
                            fullWidth
                          >
                            Stop Impersonation
                          </Button>
                        ) : (
                          <>
                            <TextField
                              label="User Email"
                              type="email"
                              fullWidth
                              value={adminEmail}
                              onChange={(e) => {
                                setAdminEmail(e.target.value);
                                setAdminError(null);
                                setAdminSuccess(null);
                              }}
                              error={!!adminError}
                              helperText={adminError}
                            />
                            <Button
                              onClick={handleImpersonate}
                              variant="contained"
                              fullWidth
                              sx={{ mt: 1 }}
                              disabled={!adminEmail.trim()}
                            >
                              Impersonate User
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleLogin("redirect")}
                  variant="outlined"
                  fullWidth
                >
                  Sign in
                </Button>
              </>
            )}
          </DialogContent>
          {isAuthenticated && (
            <DialogActions>
              <Button onClick={handleMenuClose}>Cancel</Button>
              <Button onClick={() => handleLogout("redirect")} color="error">
                Sign out
              </Button>
            </DialogActions>
          )}
        </Dialog>
      ) : (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          disableAutoFocusItem
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          {isAuthenticated
            ? [
                account && (
                  <MenuItem key="account-info" disabled>
                    <Typography variant="body2" color="text.primary">
                      {account.username || account.name}
                    </Typography>
                  </MenuItem>
                ),
                isImpersonating && (
                  <MenuItem key="impersonation-info" disabled>
                    <Typography variant="body2" color="text.secondary">
                      Impersonating: {impersonatedUserEmail || ""}
                    </Typography>
                  </MenuItem>
                ),
                <MenuItem key="logout" onClick={() => handleLogout("redirect")}>
                  <Typography color="error">Sign out</Typography>
                </MenuItem>,

                ...(user?.isAdmin
                  ? [
                      <Divider key="admin-divider" />,
                      <MenuItem key="admin-header" disabled>
                        <Typography variant="subtitle2" color="text.primary">
                          Admin Actions
                        </Typography>
                      </MenuItem>,
                      <MenuItem
                        key="email-templates"
                        onClick={() => {
                          navigate("/settings/email-templates");
                          handleMenuClose();
                        }}
                      >
                        <Typography color="text.primary">
                          Email Templates
                        </Typography>
                      </MenuItem>,
                      <Divider key="tabs-divider" />,
                      <MenuItem
                        key="tabs"
                        disableRipple
                        sx={{
                          flexDirection: "column",
                          alignItems: "stretch",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        <Tabs
                          value={adminTab}
                          onChange={(e, v) => setAdminTab(v)}
                          variant="fullWidth"
                        >
                          <Tab label="Impersonate" />
                        </Tabs>
                      </MenuItem>,
                      ...(adminSuccess
                        ? [
                            <MenuItem key="admin-success" disabled>
                              <Typography variant="body2" color="success.main">
                                {adminSuccess}
                              </Typography>
                            </MenuItem>,
                          ]
                        : []),
                      ...(isImpersonating
                        ? [
                            <MenuItem
                              key="stop-impersonate"
                              onClick={handleStopImpersonating}
                            >
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                fullWidth
                              >
                                Stop Impersonation
                              </Button>
                            </MenuItem>,
                          ]
                        : [
                            <MenuItem key="impersonate-input" disableRipple>
                              <TextField
                                size="small"
                                placeholder="Enter email to impersonate"
                                value={adminEmail}
                                onChange={(e) => {
                                  setAdminEmail(e.target.value);
                                  setAdminError(null);
                                  setAdminSuccess(null);
                                }}
                                error={!!adminError}
                                helperText={adminError}
                                fullWidth
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </MenuItem>,
                            <MenuItem
                              key="impersonate-button"
                              onClick={handleImpersonate}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                disabled={!adminEmail.trim()}
                              >
                                Impersonate User
                              </Button>
                            </MenuItem>,
                          ]),
                    ]
                  : []),
              ]
            : []}
        </Menu>
      )}
    </>
  );
};

export default UserAccountMenu;
